import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Box,
  Select,
  Text,
  Alert,
  AlertIcon,
  Spinner,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { LinkIcon } from '@chakra-ui/icons';

import { useGroupedBillingSync } from 'hooks/useGroupedBillingSync';

import { getCarrierSettings } from '../helpers';
import { PAYMENT_PERIOD_LABELS } from '../constants';

import type { PaymentOption } from '../types';
import type { APILinkedPolicy } from 'types/api.types';

interface GroupedBillingSyncSelectorProps {
  isDisabled?: boolean;
  onBillingSourceChange?: (sourcePolicy: APILinkedPolicy | null) => void;
  paymentConfig: PaymentOption[];
  policyGroupId: string | null;
  currentBinderHasOwnBilling?: boolean;
}

type PaymentPeriod = 'annual' | 'monthly' | '2_pay' | '3_pay' | '4_pay';
type PaymentMethodType = 'eft' | 'credit_card' | 'agency_bill' | 'direct_bill';

const isPaymentMethodCompatible = (
  currentCarrierConfig: PaymentOption | undefined,
  sourcePaymentPlan: string | null,
  sourcePaymentMethod: string | null,
): boolean => {
  if (!currentCarrierConfig || !sourcePaymentPlan || !sourcePaymentMethod) {
    return false;
  }

  if (!(sourcePaymentPlan in PAYMENT_PERIOD_LABELS)) return false;

  const periodKey = sourcePaymentPlan as PaymentPeriod;
  const periodConfig = currentCarrierConfig.payment_methods[periodKey];
  if (!periodConfig) return false;

  const methodKey = sourcePaymentMethod as PaymentMethodType;
  return periodConfig[methodKey] === true;
};

const GroupedBillingSyncSelector = ({
  isDisabled,
  onBillingSourceChange,
  paymentConfig,
  policyGroupId,
  currentBinderHasOwnBilling = false,
}: GroupedBillingSyncSelectorProps): JSX.Element | null => {
  const { uuid } = useParams<{ uuid: string }>();
  const [error, setError] = useState<string | null>(null);

  const {
    billingSourcePolicyUuid,
    linkedPolicies,
    carrierName,
    isLoading,
    setGroupedBillingSource,
    isSettingGroupedBillingSource,
  } = useGroupedBillingSync(uuid, { policyGroupId });

  const currentCarrierSettings = useMemo(() => {
    if (!carrierName) return undefined;
    return getCarrierSettings(carrierName, paymentConfig);
  }, [carrierName, paymentConfig]);

  const availableSourcePolicies = useMemo(() => {
    if (!linkedPolicies) return [];

    return linkedPolicies.filter((policy) => {
      if (policy.billing_source_policy_uuid) return false;

      if (!policy.payment_plan || !policy.payment_method) return false;

      return isPaymentMethodCompatible(
        currentCarrierSettings,
        policy.payment_plan,
        policy.payment_method,
      );
    });
  }, [linkedPolicies, currentCarrierSettings]);

  if (isLoading || !policyGroupId) {
    return null;
  }

  if (!linkedPolicies || linkedPolicies.length === 0) {
    return null;
  }

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const selectedSourcePolicy = value
      ? linkedPolicies.find((policy) => policy.uuid === value) || null
      : null;

    setError(null);
    setGroupedBillingSource(value === '' ? null : value, {
      onSuccess: () => {
        onBillingSourceChange?.(selectedSourcePolicy);
      },
      onError: (err: any) => {
        setError(err?.error || 'Failed to update billing source');
      },
    });
  };

  const getSourcePolicyLabel = (policy: APILinkedPolicy): string => {
    const name = policy.client_name || 'Unknown';
    return `${policy.type} - ${name}`;
  };

  // Get the selected policy's label for display when billing is synced
  const getSelectedPolicyLabel = (): string | null => {
    if (!billingSourcePolicyUuid) return null;
    const policy = linkedPolicies.find(
      (p) => p.uuid === billingSourcePolicyUuid,
    );
    return policy ? getSourcePolicyLabel(policy) : null;
  };

  if (availableSourcePolicies.length === 0 && !billingSourcePolicyUuid) {
    if (currentBinderHasOwnBilling) {
      return null;
    }

    return (
      <Box mb={8}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Billing sync is currently unavailable because there are no policies
            with compatible payment methods. Please ensure that the linked
            policies have valid payment information and are supported by the
            current carrier’s billing options.
          </Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box mb={8} borderColor={billingSourcePolicyUuid ? 'blue.300' : 'gray.200'}>
      <HStack mb={2}>
        <Icon as={LinkIcon} color="blue.500" />
        <Text fontWeight="bold" color="blue.700">
          Multi-Binder Billing
        </Text>
      </HStack>

      <Text fontSize="sm" color="gray.600" mb={3}>
        This binder is part of a multi-binder group. You can use the same
        billing information as another linked binder.
      </Text>

      <Select
        value={billingSourcePolicyUuid || ''}
        onChange={handleChange}
        isDisabled={isDisabled || isSettingGroupedBillingSource}
        placeholder="Use own billing information"
        bg="white"
      >
        {availableSourcePolicies.map((policy) => (
          <option key={policy.uuid} value={policy.uuid}>
            Same as {getSourcePolicyLabel(policy)}
          </option>
        ))}
      </Select>

      {isSettingGroupedBillingSource && (
        <HStack mt={2}>
          <Spinner size="sm" />
          <Text fontSize="sm" color="gray.500">
            Updating...
          </Text>
        </HStack>
      )}

      {error && (
        <Alert status="error" mt={2} borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">{error}</Text>
        </Alert>
      )}

      {billingSourcePolicyUuid && (
        <Alert status="info" mt={3} borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            Billing information will be read from{' '}
            <strong>{getSelectedPolicyLabel()}</strong>.
          </Text>
        </Alert>
      )}
    </Box>
  );
};

export default GroupedBillingSyncSelector;
